import { useState, useRef, useEffect, useCallback } from 'react';
import { Play, Pause, Volume2, VolumeX, Download } from 'lucide-react';
import { Button } from './ui/button';
import { Slider } from './ui/slider';

export default function AudioPlayer({ src, fallbackDuration }) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const [wasPlaying, setWasPlaying] = useState(false);
  const audioRef = useRef(null);

  const togglePlay = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      audio.play()
        .then(() => setIsPlaying(true))
        .catch(e => {
          console.error('Play failed:', e);
          setIsPlaying(false);
        });
    }
  }, [isPlaying]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateTime = () => {
      if (!isDragging) {
        setCurrentTime(audio.currentTime);
      }
    };

    const resolveDuration = () => {
      // answered_seconds is the authoritative call duration. Prefer it so the
      // player matches the call metric (the decoded audio.duration is often a
      // fraction shorter, e.g. 29.x, and would floor to a mismatching 0:29).
      // Fall back to the media element's own duration only when it isn't known.
      const known = Number(fallbackDuration);
      if (Number.isFinite(known) && known > 0) {
        setDuration(known);
        return;
      }
      const d = audio.duration;
      if (Number.isFinite(d) && d > 0) {
        setDuration(d);
      }
    };

    const handleLoadedMetadata = resolveDuration;
    const handleDurationChange = resolveDuration;

    const handleEnded = () => {
      setIsPlaying(false);
    };

    const handleVolumeChange = () => {
      setVolume(audio.volume);
    };

    const handleRateChange = () => {
      setPlaybackRate(audio.playbackRate);
    };

    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);

    audio.addEventListener('timeupdate', updateTime);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('durationchange', handleDurationChange);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('volumechange', handleVolumeChange);
    audio.addEventListener('ratechange', handleRateChange);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);

    return () => {
      audio.removeEventListener('timeupdate', updateTime);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('durationchange', handleDurationChange);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('volumechange', handleVolumeChange);
      audio.removeEventListener('ratechange', handleRateChange);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
    };
  }, [isDragging, fallbackDuration]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.volume = volume;
    audio.playbackRate = playbackRate;
  }, [volume, playbackRate]);

  const handleProgressChange = (value) => {
    const audio = audioRef.current;
    if (audio && isPlaying) {
      setWasPlaying(true);
      audio.pause();
    }
    setIsDragging(true);
    if (audio) {
      audio.currentTime = (value[0] / 100) * duration;
      setCurrentTime(audio.currentTime);
    }
  };

  const handleProgressCommit = () => {
    setIsDragging(false);
    const audio = audioRef.current;
    if (audio && wasPlaying) {
      audio.play().then(() => setIsPlaying(true));
      setWasPlaying(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  const handleDownload = async () => {
    try {
      const response = await fetch(src);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'recording.mp3';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Download failed:', e);
    }
  };

  const playbackRates = [0.5, 0.75, 1, 1.25, 1.5, 2];

  return (
    <div className="w-full bg-card border border-border rounded-lg p-4 space-y-3" data-testid="call-detail-audio-player">
      <audio
        ref={audioRef}
        src={src}
        preload="metadata"
        className="sr-only"
      />

      {/* Controls */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="sm"
          className="w-12 h-12 p-0 rounded-full hover:bg-primary/20 group"
          onClick={togglePlay}
          data-testid="call-detail-play-pause-btn"
        >
          {isPlaying ? <Pause className="w-6 h-6 group-hover:text-white" /> : <Play className="w-6 h-6 group-hover:text-white" />}
        </Button>

        <div className="flex-1 flex items-center gap-3">
          <span className="text-sm font-mono text-muted-foreground min-w-[5rem]">
            {formatTime(currentTime)} / {formatTime(duration)}
          </span>

          <Slider
            value={[progress]}
            onValueChange={handleProgressChange}
            onValueCommit={handleProgressCommit}
            max={100}
            step={0.1}
            className="flex-1 h-2 [&>span]:cursor-grab hover:[&>span]:cursor-grab [&>[data-state=dragging]]:cursor-grabbing"
          />
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            className="w-10 h-10 p-0 rounded-full"
            onClick={handleDownload}
            title="Download"
          >
            <Download className="w-4 h-4" />
          </Button>
          
          <select
            value={playbackRate}
            onChange={(e) => {
              const rate = parseFloat(e.target.value);
              setPlaybackRate(rate);
              const audio = audioRef.current;
              if (audio) audio.playbackRate = rate;
            }}
            className="h-10 px-3 text-sm border border-border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring"
          >
            {playbackRates.map(rate => (
              <option key={rate} value={rate}>
                {rate}x
              </option>
            ))}
          </select>

          <div className="flex items-center gap-2 w-24">
            <Button
              variant="ghost"
              size="sm"
              className="w-9 h-9 p-0 rounded-full"
              onClick={() => {
                const audio = audioRef.current;
                if (audio) {
                  const newVol = audio.volume > 0 ? 0 : 1;
                  audio.volume = newVol;
                  setVolume(newVol);
                }
              }}
            >
              {volume > 0 ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
            </Button>
            <Slider
              value={[volume * 100]}
              onValueChange={(value) => setVolume(value[0] / 100)}
              max={100}
              step={1}
              className="h-4 flex-1"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
